import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { RefObject } from 'react'
import isEmpty from 'lodash/isEmpty'
import { useMeasure, useScroll } from 'react-use'
import type {
  DirectionEnum,
  PivotDateRange,
  PivotGrandTotalsEnum,
  PivotLayoutColumnTarget,
  PivotLayoutColumnWidth,
  PivotLayoutField,
  PivotLayoutFilter,
  PivotLayoutMemberSort,
  PivotLayoutValueColumn,
  PivotLayoutValueSort,
} from '@ztechapps/common.contract'
import type { PivotCellRenderer, PivotCellTextRenderer } from '../constants/types'
import type { PivotData, PivotExpandState, PivotLeafColumn, PivotResult } from '../pivot/types'
import { Table } from '@ztechapps/webui.data-tools'
import { ShowIf, TooltipPopOver } from '@ztechapps/webui.util'
import { PIVOT_GRAND_TOTAL_LABEL, PIVOT_TEXT_ALIGN_CLASSES } from '../pivot/constants'
import { computePivotRowRange } from '../pivot/computePivotRowRange'
import { formatPivotValue } from '../pivot/formatPivotValue'
import { isPivotExpanded } from '../pivot/isPivotExpanded'
import { listPivotColumns } from '../pivot/listPivotColumns'
import { listPivotRows } from '../pivot/listPivotRows'
import { hasPivotGrandTotalRow } from '../pivot/pivotGrandTotals.utils'
import { findPivotColumnWidth } from '../pivot/pivotLayout.utils'
import { toPivotLayoutValueColumn } from '../pivot/pivotValueColumn.utils'
import { usePivotViewerContext } from '../PivotViewer/Context'
import { PivotGridProvider } from './Context'
import { PivotGridHead } from './PivotGridHead'
import { PivotGridRow } from './PivotGridRow'
import { usePivotCellSelection } from './usePivotCellSelection'

/** Props for the pivot grid table. */
export type PivotGridProps = {
  result: PivotResult
  expandState: PivotExpandState
  showSubtotals: boolean
  showGrandTotals: PivotGrandTotalsEnum
  maxHeight: string
  renderCell?: PivotCellRenderer
  renderCellText?: PivotCellTextRenderer
  onToggle: (key: string) => void
  data: PivotData
  filters: PivotLayoutFilter[]
  timeZone: string
  onFilter: (field: PivotLayoutField, excludedValues: string[], dateRange?: PivotDateRange) => void
  columnWidths: PivotLayoutColumnWidth[]
  onColumnWidth: (target: PivotLayoutColumnTarget, width?: number) => void
  memberSorts: PivotLayoutMemberSort[]
  onMemberSort: (field: PivotLayoutField, direction?: DirectionEnum) => void
  valueSort?: PivotLayoutValueSort
  onValueSort: (valueColumn: PivotLayoutValueColumn) => void
  rootRef: RefObject<HTMLDivElement | null>
}

/** Scrollable pivot table with expandable rows and grand totals. */
export const PivotGrid = ({
  result,
  expandState,
  showSubtotals,
  showGrandTotals,
  maxHeight,
  renderCell,
  renderCellText,
  onToggle,
  data,
  filters,
  timeZone,
  onFilter,
  columnWidths,
  onColumnWidth,
  memberSorts,
  onMemberSort,
  valueSort,
  onValueSort,
  rootRef,
}: PivotGridProps) => {
  const { headerRows, leafColumns } = useMemo(
    () => listPivotColumns({ result, showSubtotals, showGrandTotals }),
    [result, showSubtotals, showGrandTotals]
  )
  const rows = useMemo(
    () => listPivotRows({ result, expandState, showSubtotals }),
    [result, expandState, showSubtotals]
  )
  const scrollRef = useRef<HTMLDivElement>(null)
  const { x: scrollLeft, y: scrollTop } = useScroll(scrollRef)
  // the range must be windowed against the SCROLL CONTAINER's own height (`maxHeight`),
  // not the browser window's — the window is taller, so `end` overshoot rendered more real rows
  // than fit, undersizing the bottom spacer and leaving the sticky `tfoot` (table.module.scss)
  // nothing left to stick against once the true scroll position ran past that shortened content.
  // `useMeasure` sits on a wrapper `<div>` around `<Table>` rather than sharing `scrollRef`'s node
  // (libs/webui/data-tools/src/DataTree/Layout.tsx:15,20) — the wrapper's rendered height tracks
  // `Table`'s own scroll area within a few px (its `padding-bottom`/`padding-right`,
  // Scrollable/scrollable.module.scss), a gap the `PIVOT_GRID_OVERSCAN` rows rendered above and
  // below the visible range (computePivotRowRange.ts) absorb easily.
  const [measureRef, { height: viewportHeight }] = useMeasure<HTMLDivElement>()
  const [rowHeight, setRowHeight] = useState(0)
  const [measuredWidths, setMeasuredWidths] = useState<{
    leafColumns: PivotLeafColumn[]
    columnWidths: PivotLayoutColumnWidth[]
    widths: number[]
  }>({ leafColumns: [], columnWidths: [], widths: [] })
  const { start, end } = computePivotRowRange({ scrollTop, viewportHeight, rowHeight, rowCount: rows.length })

  useLayoutEffect(() => {
    const row = scrollRef.current?.querySelector('tbody tr[aria-rowindex]')
    if (!row) return
    // Re-measuring on every rendered-slice change feeds `start` / `end` back into `rowHeight`
    // with no fixed point — each new height re-slices, and the next slice measures another
    // height — so a sub-pixel wobble re-renders forever. Keep the old height while the new one
    // lands within a pixel of it; the overscan rows absorb the rounding.
    setRowHeight((prev) => {
      const height = row.getBoundingClientRect().height
      return Math.abs(height - prev) < 1 ? prev : height
    })
    setMeasuredWidths((prev) => {
      const stored = prev.leafColumns === leafColumns && prev.columnWidths === columnWidths ? prev.widths : []
      let grew = false
      const widths = Array.from(row.children).map((cell, index) => {
        const width = Math.max(stored[index] ?? 0, cell.getBoundingClientRect().width)
        if (width > (stored[index] ?? 0)) grew = true
        return width
      })
      if (!grew && prev.leafColumns === leafColumns && prev.columnWidths === columnWidths) return prev
      return { leafColumns, columnWidths, widths }
    })
  }, [rows, leafColumns, start, end, columnWidths])

  // the measured widths' index space: one Rows level each, then one leaf column each
  const columnTargets = useMemo<PivotLayoutColumnTarget[]>(
    () => [
      ...result.rowFields.map((rowField) => ({ rowField })),
      ...leafColumns.map((column) => ({ valueColumn: toPivotLayoutValueColumn(result, column) })),
    ],
    [result, leafColumns]
  )
  const [draft, setDraft] = useState<{ index: number; width: number }>()
  const fixedWidths = useMemo(
    () =>
      columnTargets.map((target, index) =>
        draft?.index === index ? draft.width : findPivotColumnWidth(columnWidths, target)?.width
      ),
    [columnTargets, columnWidths, draft]
  )
  const measured =
    measuredWidths.leafColumns === leafColumns && measuredWidths.columnWidths === columnWidths
      ? measuredWidths.widths
      : []

  const isExpanded = (key: string): boolean => isPivotExpanded(expandState, key)

  // pinned by the scrollable path's sticky tfoot; the body cells' alignment keeps the columns uniform
  const grandTotalFoot =
    hasPivotGrandTotalRow(showGrandTotals) && !isEmpty(result.rowFields) ? (
      // `-1px` sinks the stuck footer 1px past the scroll box (clipped there) so its cell backgrounds
      // cover the fractional device pixel the box edge leaves at a fractional container height
      <tfoot style={{ insetBlockEnd: -1 }}>
        <tr className="fw-semibold" aria-rowindex={headerRows.length + rows.length + 1}>
          <th scope="row" colSpan={result.rowFields.length} className="text-nowrap bg-body-tertiary fw-semibold">
            {result.rowFields.length === 1 && fixedWidths[0] !== undefined ? (
              <div className="d-inline-block align-top text-truncate" style={{ width: fixedWidths[0] }}>
                {PIVOT_GRAND_TOTAL_LABEL}
              </div>
            ) : (
              PIVOT_GRAND_TOTAL_LABEL
            )}
          </th>
          {leafColumns.map((column, index) => {
            const format = result.measureFormats[column.measureIndex]
            const width = fixedWidths[result.rowFields.length + index]
            const value = formatPivotValue(
              result.getValue(result.rowRoot.key, column.columnKey, column.measureIndex),
              format
            )
            return (
              <td key={column.key} className={`${PIVOT_TEXT_ALIGN_CLASSES[format.textAlign]} text-nowrap`}>
                {width === undefined ? (
                  value
                ) : (
                  <div className="d-inline-block align-top text-truncate" style={{ width }}>
                    {value}
                  </div>
                )}
              </td>
            )
          })}
        </tr>
      </tfoot>
    ) : undefined

  // scroll fires per pixel, so the slice keeps its identities between range changes and the rows stay memoised
  const dataArray = useMemo(
    () => [
      ...(start > 0 ? [{ _id: 'pivot-spacer-top', spacerHeight: start * rowHeight }] : []),
      ...rows.slice(start, end).map((item, index) => ({
        ...item,
        ariaRowIndex: headerRows.length + start + index + 1,
        rowIndex: start + index,
      })),
      ...(end < rows.length ? [{ _id: 'pivot-spacer-bottom', spacerHeight: (rows.length - end) * rowHeight }] : []),
    ],
    [rows, headerRows.length, start, end, rowHeight]
  )

  const { container } = usePivotViewerContext()
  const { gridRef, gridProps, isCellSelected, summary } = usePivotCellSelection({
    result,
    rows,
    leafColumns,
    headerRowCount: headerRows.length,
    renderCellText,
    rootRef,
    scrollRef,
    scrollTop,
    scrollLeft,
  })

  return (
    <PivotGridProvider
      {...{
        result,
        headerRows,
        leafColumns,
        isExpanded,
        onToggle,
        renderCell,
        data,
        filters,
        timeZone,
        onFilter,
        measuredWidths: measured,
        fixedWidths,
        onColumnDrag: (index, width) => setDraft({ index, width }),
        onColumnResize: (index, width) => {
          setDraft(undefined)
          onColumnWidth(columnTargets[index], width)
        },
        onColumnReset: (index) => onColumnWidth(columnTargets[index]),
        memberSorts,
        onMemberSort,
        valueSort,
        onValueSort,
        isCellSelected,
      }}
    >
      <div ref={measureRef}>
        <div ref={gridRef} {...gridProps} className="position-relative">
          <ShowIf condition={!!summary?.anchor}>
            {/* ONE overlay at grid level — the anchor box tracks the selection's cell per step, so
            this node updates its text and position in place instead of remounting on a moving
            cell with an unmeasured first frame. `trigger={[]}` leaves it fully parent-driven
            (no hover/focus churn against `show`), `flip={false}` pins it below the anchor, and
            `clickThrough` keeps a drag crossing it reaching the cell beneath. */}
            <TooltipPopOver
              tooltipBody={summary?.text}
              show={summary?.visible ?? false}
              trigger={[]}
              placement="bottom-start"
              flip={false}
              container={container}
              clickThrough
            >
              <div
                data-testid="pivot-selection-summary"
                className="pe-none position-absolute"
                style={{
                  top: summary?.anchor?.top,
                  left: summary?.anchor?.left,
                  width: summary?.anchor?.width,
                  height: summary?.anchor?.height,
                }}
              />
            </TooltipPopOver>
          </ShowIf>
          <Table
            scrollable
            showThumb={false}
            // `pb-0` (Bootstrap `!important`) overrides the shared Scrollable's `padding-bottom: 10px`
            // (`scrollable.module.scss`) for this table only — that padding pins the sticky `tfoot`'s
            // `bottom: 0` 10px above the scroll area's real bottom edge, leaving a gap body rows show
            // through mid-scroll.
            scrollClassName="overflow-x-auto pb-0"
            height={maxHeight}
            tableClassName="table table-sm table-bordered mb-0"
            scrollRef={scrollRef}
            dataArray={dataArray}
            aria-rowcount={headerRows.length + rows.length + (grandTotalFoot ? 1 : 0)}
            head={<PivotGridHead />}
            foot={grandTotalFoot}
            row={PivotGridRow}
          />
        </div>
      </div>
    </PivotGridProvider>
  )
}
